import { queryParamsToString } from "@/utils";
import { EndpointDefinition, TagDescription } from "@reduxjs/toolkit/query/react";
import omit from "lodash/omit";
import { binaryDownloadResponseHandler } from "./api.utils";
import {
  ApiV1ProtocolSetsProtocolQuestionsListApiArg,
  ApiV1ProtocolSetsProtocolQuestionsListApiResponse,
  ApiV1StandardsRequirementsLiteListApiArg,
  ApiV1StandardsRequirementsLiteListApiResponse,
  AssignedThemesRead,
  CorporationRead,
  UserPreferenceRead,
  WhoAmIRead,
  addTagTypes,
  api as generatedApi,
} from "./generatedApi";

export * from "./generatedApi";

/**
 * Function to enhance a download endpoint with the binaryDownloadResponseHandler & more
 * Exists as a separate function in order to preserve the underlying endpoint definition
 * and not have to rewrite it (which would therefore decouple it from the OpenAPI definition => bug prone)
 *
 * Leaving as heavily untyped "any" because RTK's inner function definitions are extremely complicated
 * I think this is tolerable because we're enhancing existing endpoints rather than creating new ones
 */
function enhanceDownloadEndpoint(endpoint: EndpointDefinition<any, any, any, any>) {
  const originalQuery = endpoint.query;
  endpoint.query = (queryArg: any) => ({
    ...(originalQuery ? originalQuery(queryArg) : {}),
    cache: "no-cache",
    responseHandler: binaryDownloadResponseHandler,
  });
  endpoint.invalidatesTags = [];
}

export const api = generatedApi
  .enhanceEndpoints({
    endpoints: {
      apiV1AssessmentsAssessmentsDestroy: {
        invalidatesTags: ["Assessments", "Reports", "Tasks"],
      },
      apiV1AssessmentsAssessmentsList: {
        providesTags: (result, error, page) =>
          result
            ? [
                // Provides a tag for each post in the current page,
                // as well as the 'PARTIAL-LIST' tag.
                ...result.results.map(({ id }) => ({ type: "Assessments" as const, id })),
                { type: "Assessments", id: "PARTIAL-LIST" },
              ]
            : [{ type: "Assessments", id: "PARTIAL-LIST" }],
      },
      apiV1AssessmentsAssessmentsCompleteCreate: {
        invalidatesTags: ["Assessments", "Reports", "Tasks"],
      },
      apiV1AssessmentsAssessmentsFavoriteCreate: {
        invalidatesTags: (result, error, id) => [
          { type: "Assessments" as const, id: id.assessmentId },
          { type: "Assessments", id: "PARTIAL-LIST" },
        ],
      },
      apiV1AssessmentsAssessmentsFavoriteDestroy: {
        invalidatesTags: (result, error, id) => [
          { type: "Assessments" as const, id: id.assessmentId },
          { type: "Assessments", id: "PARTIAL-LIST" },
        ],
      },
      apiV1ProtocolSetsInterlinkCreate: {
        invalidatesTags(result) {
          if (!result) {
            return [];
          }
          return result.map(protocol => ({ type: "Protocols" as const, id: protocol.id }));
        },
      },
      apiV1AssessmentsAssessmentsSiteAssessmentsVerificationCreate: {
        invalidatesTags: ["Site Assessments", "Assessments"],
      },
      apiV1StandardsProtocolsRetrieve: {
        providesTags(req) {
          return req ? [{ type: "Protocols", id: req.id }] : [{ type: "Protocols" }];
        },
      },
      apiV1StandardsRequirementsUnlinkFromProtocolQuestionCreate: {
        invalidatesTags: ["Protocols", "Protocol Sets", "Requirements"],
      },
      apiV1CorporationsCreate: {
        invalidatesTags: [],
        async onQueryStarted(_, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(
              generatedApi.util.updateQueryData("apiV1CorporationsList", undefined, (draft: CorporationRead[]) => [
                ...draft,
                data,
              ]),
            );
          } catch (_err) {
            // pass
          }
        },
      },
      apiV1CorporationsUpdate: {
        invalidatesTags: [],
        async onQueryStarted({ id: dataId }, { dispatch, queryFulfilled }) {
          try {
            const { data: updatedCorp } = await queryFulfilled;
            // pessimistic update:
            dispatch(
              generatedApi.util.updateQueryData("apiV1CorporationsList", undefined, (draft: CorporationRead[]) => {
                const index = draft.findIndex(({ id }) => id === dataId);
                return [...draft.slice(0, index), updatedCorp, ...draft.slice(index + 1)];
              }),
            );
          } catch {
            /* empty */
          }
        },
      },
      apiV1AssessmentsAssessmentsSiteAssessmentsAssignCreate: {
        invalidatesTags: ["Tasks"],
        async onQueryStarted({ assessmentId, id, assignThemes }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            generatedApi.util.updateQueryData(
              "apiV1AssessmentsAssessmentsSiteAssessmentsAssignedRetrieve",
              { assessmentId, id },
              (draft: AssignedThemesRead) => {
                const [[themeId, value]] = Object.entries(assignThemes.assignment);
                if (value.length === 0) {
                  // we shouldn't add themeId to unassigned if was added before
                  if (draft!.unassigned_themes.includes(parseInt(themeId, 10))) {
                    return draft;
                  }
                  return {
                    assigned_themes: omit({ ...draft.assigned_themes }, [themeId]),
                    unassigned_themes: [...draft.unassigned_themes, parseInt(themeId, 10)],
                  };
                }
                return {
                  assigned_themes: {
                    ...draft.assigned_themes,
                    ...assignThemes.assignment,
                  },
                  unassigned_themes: [...draft.unassigned_themes].filter(
                    theme => !Object.keys(assignThemes.assignment).includes(theme.toString()),
                  ),
                };
              },
            ),
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        },
      },
      apiV1UserPreferencesUpdate: {
        invalidatesTags: [],
        async onQueryStarted({ tableName, userPreference }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            generatedApi.util.updateQueryData("apiV1UserPreferencesList", undefined, (draft: UserPreferenceRead[]) => {
              const index = draft.findIndex(preference => preference.table_name === tableName);
              if (index !== -1) {
                return [
                  ...draft.slice(0, index),
                  { table_name: tableName, ...userPreference },
                  ...draft.slice(index + 1),
                ];
              }
              return [...draft, { table_name: tableName, ...userPreference }];
            }),
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        },
      },
      apiV1UserManagementProfileUpdate: {
        async onQueryStarted({ userProfile }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            generatedApi.util.updateQueryData("whoamiRetrieve", undefined, (draft: WhoAmIRead) => ({
              ...draft,
              ...userProfile,
            })),
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        },
      },
      apiV1StandardsProtocolsPartialUpdate: {
        invalidatesTags: ["Requirements", "Protocols", "Protocol Sets"],
      },
      apiV1AssessmentsAssessmentsReviewAssignmentsCreate: {
        invalidatesTags: ["Review Assignments", "Assessments"],
      },
      apiV1AssessmentsAssessmentsReviewAssignmentsUpdate: {
        invalidatesTags: ["Review Assignments", "Assessments"],
      },
      apiV1AssessmentsAssessmentsUpdate: {
        invalidatesTags: ["Review Assignments", "Assessments"],
      },
      apiV1AssessmentsAssessmentsSiteAssessmentsApprovalCreate: {
        invalidatesTags: ["Assessments", "Site Assessments"],
      },
      apiV1StandardsProtocolsUpdate: {
        invalidatesTags: ["Protocols", "Protocol Sets"],
      },
      apiV1AssessmentsAssessmentsSiteAssessmentsCommentsList: {
        providesTags: (_result, _error, arg) => [
          { type: "Site Assessments Comments" },
          { type: "Site Assessments Comments", id: arg.id },
        ],
      },
      apiV1AssessmentsAssessmentsCommentsList: {
        providesTags: (_result, _error, arg) => [
          { type: "Assessments Comments" },
          { type: "Assessments Comments", id: arg.assessmentId },
        ],
      },
    },
  })
  // Download Endpoints
  .enhanceEndpoints({
    endpoints: {
      // ### Task Evidence ### - leaving as is - different file download approach
      // ### Site Assessment Approval ### - leaving as is - different file download approach
      // ### Reports ###
      // Assessment Report
      apiV1ReportsAssessmentsSiteAssessmentsDownloadCsvRetrieve: enhanceDownloadEndpoint,
      apiV1ReportsAssessmentsSiteAssessmentsDownloadXlsxRetrieve: enhanceDownloadEndpoint,
      // Standard Report
      apiV1ReportsAssessmentsSiteAssessmentsStandardsRollupDownloadCsvRetrieve: enhanceDownloadEndpoint,
      apiV1ReportsAssessmentsSiteAssessmentsStandardsRollupDownloadXlsxRetrieve: enhanceDownloadEndpoint,
      apiV1ReportsAssessmentsSiteAssessmentsStandardsIrmaXlsxRetrieve: enhanceDownloadEndpoint,
      // Custom Report
      apiV1ReportsCustomDownloadCsvRetrieve: enhanceDownloadEndpoint,
      apiV1ReportsCustomDownloadXlsxRetrieve: enhanceDownloadEndpoint,
      // ### Mapping Tool ###
      apiV1ProtocolSetsCompareList: enhanceDownloadEndpoint,
      apiV1ProtocolSetsProtocolQuestionsDownloadXlsxRetrieve: enhanceDownloadEndpoint,
      apiV1ProtocolSetsProtocolQuestionsDownloadMappingXlsxRetrieve: enhanceDownloadEndpoint,
      // ### AUP ###
      apiV1AssessmentsUpdatePanelAssessmentsDryRunRetrieve: enhanceDownloadEndpoint,
      apiV1AssessmentsUpdatePanelAssessmentsHistoryRetrieve: enhanceDownloadEndpoint,
      // ### Admin ###
      apiV1AdminUserReportRetrieve: enhanceDownloadEndpoint,
      apiV1AdminMappingJobsDownloadRetrieve: enhanceDownloadEndpoint,
    },
  })
  .injectEndpoints({
    endpoints: build => ({
      apiV1ProtocolSetsProtocolQuestionsInfiniteList: build.query<
        ApiV1ProtocolSetsProtocolQuestionsListApiResponse,
        ApiV1ProtocolSetsProtocolQuestionsListApiArg
      >({
        query: queryArg => ({
          url: `/api/v1/protocol_sets/${queryArg.id}/protocol_questions`,
          params: {
            ordering: queryArg.ordering,
            page: queryArg.page,
            page_size: queryArg.pageSize,
            search: queryArg.search,
            theme: queryArg.theme,
            sub_theme: queryArg.subTheme,
            ft_search: queryArg.ftSearch,
            requirement__not: queryArg.requirementNot,
            protocol_question__not: queryArg.protocolQuestionNot,
          },
        }),
        serializeQueryArgs: ({ endpointName, queryArgs }) => {
          // We don't want to use page in the cache key
          const { requirementNot, protocolQuestionNot, theme, subTheme, page: _, ...rest } = queryArgs;

          const params = {
            ...rest,
            protocol_question__not: protocolQuestionNot?.join(","),
            requirement__not: requirementNot,
            theme: theme?.join(","),
            subTheme: subTheme?.join(","),
          };

          return `${endpointName}?${queryParamsToString(params)}`;
        },
        merge: (currentCache, newItems, { arg }) => {
          if (arg.page === 1) {
            return newItems;
          }
          if (newItems?.results) {
            const itemsToAdd = newItems.results.filter(
              newItem => !currentCache?.results?.some(currentItem => currentItem.id === newItem.id),
            );
            if (currentCache) {
              currentCache.results?.push(...itemsToAdd);
              currentCache.next = newItems.next;
            }
          }
        },
        providesTags: ["Protocol Sets"],
        forceRefetch({ currentArg, previousArg }) {
          return currentArg !== previousArg;
        },
      }),
      apiV1StandardsRequirementsInfiniteList: build.query<
        ApiV1StandardsRequirementsLiteListApiResponse,
        ApiV1StandardsRequirementsLiteListApiArg
      >({
        query: queryArg => ({
          url: "/api/v1/standards/requirements/lite/",
          params: {
            is_mapped: queryArg.isMapped,
            page: queryArg.page,
            protocol: queryArg.protocol,
            protocol__not: queryArg.protocolNot,
            questions: queryArg.questions,
            search: queryArg.search,
            search_score_threshold: queryArg.searchScoreThreshold,
            standard: queryArg.standard,
          },
        }),
        serializeQueryArgs: ({ endpointName, queryArgs }) => {
          // We don't want to use page in the cache key
          const { protocolNot, standard, questions, page: _, ...rest } = queryArgs;

          const params = {
            ...rest,
            standard: standard?.join(","),
            protocol__not: protocolNot,
            questions: questions?.join(","),
          };
          return `${endpointName}?${queryParamsToString(params)}`;
        },
        merge: (currentCache, newItems) => {
          if (newItems?.results) {
            const itemsToAdd = newItems.results.filter(
              newItem => !currentCache?.results?.some(currentItem => currentItem.id === newItem.id),
            );
            if (currentCache) {
              currentCache.results?.push(...itemsToAdd);
              /* eslint-disable no-param-reassign */
              currentCache.next = newItems.next;
            }
          }
        },
        providesTags: ["Requirements"],
        forceRefetch({ currentArg, previousArg }) {
          return currentArg !== previousArg;
        },
      }),
    }),
  });

export type TagsType = TagDescription<(typeof addTagTypes)[number]>;

export const {
  usePrefetch,

  useLazyWhoamiRetrieveQuery,
  useLazyApiV1AssessmentsAssessmentsReviewAssignmentsAvailableStepsRetrieveQuery,
  useLazyApiV1CorporationsFlagsRetrieveQuery,

  // normally downloads are mutations to avoid caching, but here we're using a query to return the download link
  useLazyApiV1DownloadSiteAssessmentsAttachmentsRetrieveQuery,

  // new injected endpoints
  useApiV1StandardsRequirementsInfiniteListQuery,
  useApiV1ProtocolSetsProtocolQuestionsInfiniteListQuery,
} = api;
